import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  ListTodo, 
  Bell, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Wunderlist = () => {
  const faqData = [
    {
      question: "What happened to Wunderlist?",
      answer: "Wunderlist was acquired by Microsoft in 2015 and officially shut down on May 6, 2020. Microsoft replaced it with Microsoft To Do, built by the same team. The original Wunderlist servers are no longer operational."
    },
    {
      question: "Is there a free alternative to Wunderlist?",
      answer: "Yes! Chill Study Work offers free task management features along with study timers, reminders, and collaborative study rooms. It combines productivity tools that Wunderlist users loved with focused study sessions."
    },
    {
      question: "Can I import my Wunderlist data to CSW?",
      answer: "While Wunderlist's servers are no longer available, if you exported your data before the shutdown or migrated to Microsoft To Do, you can manually recreate your task lists in CSW's intuitive task management system."
    },
    {
      question: "How does CSW compare to Wunderlist?",
      answer: "CSW offers similar task management features (lists, reminders, due dates) plus unique study-focused tools like Pomodoro timers, study rooms with ambient sounds, and productivity tracking that Wunderlist didn't have."
    },
    {
      question: "Does CSW work on all devices like Wunderlist did?",
      answer: "Yes! CSW is a web-based platform that works seamlessly on any device with a browser - Windows, Mac, iOS, Android, and more. Your tasks and progress sync across all devices."
    },
    {
      question: "Can I collaborate with others on CSW like I did on Wunderlist?",
      answer: "Absolutely! CSW offers collaborative study rooms where you can work alongside others, share goals, and stay accountable. It takes Wunderlist's collaboration concept further with real-time study sessions."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Wunderlist Alternative - Free Task Management & Study Tools",
    "description": "Looking for a Wunderlist alternative? Wunderlist shut down in 2020 but CSW offers free Task Management, Study Timer, and Reminders to keep you productive.",
    "url": "https://chillstudy.work/wunderlist/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Wunderlist",
      "applicationCategory": "ProductivityApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "Microsoft To Do",
        "url": "https://todo.microsoft.com/"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Todoist",
        "url": "https://todoist.com/"
      }
    ],
    "keywords": "Wunderlist alternative, Wunderlist replacement, task management app, to-do list app, productivity tools, Microsoft To Do alternative"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work - Wunderlist Alternative",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Task Management with Lists",
      "Due Dates and Reminders",
      "Pomodoro Study Timer",
      "Ambient Study Rooms",
      "Progress Tracking",
      "Cross-Platform Sync",
      "Collaborative Study Sessions",
      "Focus Mode"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wunderlist Alternative - Free Task Management & Study Tools | CSW</title>
        <meta name="description" content="Looking for a Wunderlist alternative? Wunderlist shut down in 2020 but CSW offers free Task Management, Study Timer, and Reminders to keep you productive." />
        <meta name="keywords" content="Wunderlist alternative, Wunderlist replacement, task management app, to-do list app, productivity tools, study timer, reminders" />
        <link rel="canonical" href="https://chillstudy.work/wunderlist/" />
        <meta property="og:title" content="Wunderlist Alternative - Free Task Management & Study Tools" />
        <meta property="og:description" content="Wunderlist shut down in 2020. Discover CSW's free task management, study timer, and productivity tools." />
        <meta property="og:url" content="https://chillstudy.work/wunderlist/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Wunderlist shut down on May 6, 2020
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              The Best Wunderlist Alternative for Productive Minds
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Miss Wunderlist's elegant task management? Chill Study Work combines powerful to-do lists with 
              <Link to="/study-with-me/" className="text-primary hover:underline"> focused study sessions</Link>, 
              ambient sounds, and productivity tracking — all for free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/wunderlist/history/">
                  Read Wunderlist's Story
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">What Happened to Wunderlist?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <a href="https://en.wikipedia.org/wiki/Wunderlist" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Wunderlist</a> was 
              one of the most beloved to-do apps ever created. Founded by{" "}
              <a href="https://en.wikipedia.org/wiki/6Wunderkinder" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">6Wunderkinder</a> in 
              Berlin in 2011, it grew to 13 million users before{" "}
              <a href="https://news.microsoft.com/2015/06/02/microsoft-acquires-wunderlist/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft acquired it in 2015</a>.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              After the acquisition, Microsoft developed{" "}
              <a href="https://todo.microsoft.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft To Do</a> as 
              Wunderlist's successor. On May 6, 2020, Wunderlist's servers were permanently shut down.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              If you're looking for an alternative that combines task management with study-focused features, 
              CSW offers <Link to="/task-management/" className="text-primary hover:underline">task management</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">study timers</Link>,{" "}
              <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>, and{" "}
              <Link to="/group-study/" className="text-primary hover:underline">collaborative study rooms</Link>.
            </p>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Everything You Loved About Wunderlist, Plus More
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <ListTodo className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Create lists, add tasks with due dates, and organize your work just like Wunderlist. 
                  <Link to="/task-management/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <Bell className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Smart Reminders</h3>
                <p className="text-muted-foreground">
                  Never miss a deadline with customizable reminders and notifications.
                  <Link to="/reminders/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-green-500/10 rounded-lg w-fit mb-4">
                  <Clock className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Pomodoro Timer</h3>
                <p className="text-muted-foreground">
                  Built-in study timer with Pomodoro technique — something Wunderlist never had.
                  <Link to="/study-timer/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-purple-500/10 rounded-lg w-fit mb-4">
                  <Users className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Study Together</h3>
                <p className="text-muted-foreground">
                  Join live study rooms and work alongside others for accountability.
                  <Link to="/study-with-friends/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ambient Sounds</h3>
                <p className="text-muted-foreground">
                  Focus better with lo-fi beats, rain sounds, and café ambiance.
                  <Link to="/studystream/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-blue-500/10 rounded-lg w-fit mb-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Progress Tracking</h3>
                <p className="text-muted-foreground">
                  Track your study hours and completed tasks to build productive habits.
                  <Link to="/productivity-tool/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Alternatives */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Other Wunderlist Alternatives</h2>
            <p className="text-muted-foreground mb-6">
              While CSW is designed for students and focused productivity, here are other popular task management apps 
              that former Wunderlist users have migrated to:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="https://todo.microsoft.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Microsoft To Do</p>
                  <p className="text-sm text-muted-foreground">Official successor by the Wunderlist team</p>
                </div>
              </a>
              <a href="https://todoist.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Todoist</p>
                  <p className="text-sm text-muted-foreground">Powerful cross-platform task manager</p>
                </div>
              </a>
              <a href="https://ticktick.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">TickTick</p>
                  <p className="text-sm text-muted-foreground">Tasks + calendar + habit tracking</p>
                </div>
              </a>
              <a href="https://www.any.do/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Any.do</p>
                  <p className="text-sm text-muted-foreground">Sleek task and calendar app</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Blog Links */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Productivity Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/how-to-stop-procrastination/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How to Stop Procrastination</p>
                <p className="text-sm text-muted-foreground">Science-backed strategies to get things done</p>
              </Link>
              <Link to="/blog/apps-boost-organization-college/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Best Organization Apps for College</p>
                <p className="text-sm text-muted-foreground">Top apps to stay organized in school</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools every student needs</p>
              </Link>
              <Link to="/blog/apps-boost-productivity-college/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Best Productivity Apps for College</p>
                <p className="text-sm text-muted-foreground">Maximize your productivity in school</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-primary/10 rounded-lg">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card rounded-xl border border-border/50 px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 md:p-12 text-center border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Productive?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of students and professionals who use CSW to manage tasks and stay focused.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Start Using CSW Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Wunderlist" />

      <Footer />
    </div>
  );
};

export default Wunderlist;
